/**
 * Probability trees: a typed outline, repeated independent stages, or draws from a bag
 * (with or without replacement) become one tree of branches whose probabilities are
 * exact fractions, checked to add to 1 at every split.
 *
 * A branch with no probability typed takes what is left of its group ("fill in the last
 * branch"). The product along a path is the probability of that outcome (the
 * multiplication rule); the probability of an event is the sum over its outcomes (the
 * addition rule).
 */

import {
  fraction,
  ONE,
  parseProbability,
  type Rational,
  ratAdd,
  ratEquals,
  ratMul,
  ratSub,
  ratValue,
  ZERO,
} from "./rational";

export interface TreeNode {
  label: string;
  prob: Rational;
  children: TreeNode[];
}

export interface TreeLeaf {
  /** Path of child indices, "0/1". */
  id: string;
  labels: string[];
  branches: Rational[];
  /** Exact product, or null when it left the safe-integer range. */
  exact: Rational | null;
  value: number;
}

export type TreeError =
  | { kind: "empty" }
  | { kind: "line"; line: number }
  | { kind: "probability"; line: number }
  | { kind: "sum"; group: string; sum: number }
  | { kind: "blanks"; group: string }
  | { kind: "tooMany"; leaves: number }
  | { kind: "stages" }
  | { kind: "counts" }
  | { kind: "draws" };

export type TreeResult =
  | { ok: true; roots: TreeNode[] }
  | { ok: false; error: TreeError };

/** The most outcomes a diagram can show and stay readable. */
export const MAX_LEAVES = 64;
export const MAX_STAGES = 5;
export const MAX_DRAWS = 4;
export const MAX_ITEM_TYPES = 8;

/* ------------------------------------------------------------------- parsing */

interface DraftNode {
  label: string;
  /** null = left blank, to be filled with the remainder. */
  prob: Rational | null;
  line: number;
  indent: number;
  children: DraftNode[];
}

const TRAILING_PROBABILITY =
  /^(.*?)(?:\s*[:=]\s*|\s+)\(?([+]?[\d.,]+%?|\d+\s*\/\s*\d+)\)?$/;
const TRAILING_COLON = /^(.*?)\s*[:=]\s*$/;
const INDENT = /^[ \t]*/;
const LINE_BREAK = /\r?\n/;

function splitLabel(content: string): { label: string; text: string | null } {
  const colon = TRAILING_COLON.exec(content);
  if (colon) {
    return { label: colon[1].trim(), text: null };
  }
  const match = TRAILING_PROBABILITY.exec(content);
  if (match && match[1].trim() !== "") {
    return { label: match[1].trim(), text: match[2] };
  }
  return { label: content.trim(), text: null };
}

/** Lines as drafts. A token that looks like a probability but is not one is an error. */
function draftLines(text: string): DraftNode[] | TreeError {
  const drafts: DraftNode[] = [];
  const lines = text.split(LINE_BREAK);
  for (const [index, raw] of lines.entries()) {
    if (raw.trim() === "") {
      continue;
    }
    const expanded = raw.replaceAll("\t", "    ");
    const indent = (INDENT.exec(expanded) ?? [""])[0].length;
    const { label, text: probText } = splitLabel(expanded.trim());
    if (label === "") {
      return { kind: "line", line: index + 1 };
    }
    let prob: Rational | null = null;
    if (probText !== null) {
      prob = parseProbability(probText);
      if (prob === null) {
        return { kind: "probability", line: index + 1 };
      }
    }
    drafts.push({ label, prob, line: index + 1, indent, children: [] });
  }
  return drafts;
}

function nest(drafts: DraftNode[]): DraftNode[] {
  const roots: DraftNode[] = [];
  const stack: DraftNode[] = [];
  for (const draft of drafts) {
    while (stack.length > 0 && (stack.at(-1)?.indent ?? 0) >= draft.indent) {
      stack.pop();
    }
    const parent = stack.at(-1);
    if (parent) {
      parent.children.push(draft);
    } else {
      roots.push(draft);
    }
    stack.push(draft);
  }
  return roots;
}

/** Close a group of siblings: fill one blank with the remainder, then demand sum 1. */
function settle(
  group: DraftNode[],
  groupName: string
): { probs: Rational[] } | TreeError {
  const blanks = group.filter((node) => node.prob === null);
  if (blanks.length > 1) {
    return { kind: "blanks", group: groupName };
  }
  let known: Rational | null = ZERO;
  for (const node of group) {
    if (node.prob && known) {
      known = ratAdd(known, node.prob);
    }
  }
  if (known === null) {
    return approximateSum(group, groupName);
  }
  if (blanks.length === 1) {
    const rest = ratSub(ONE, known);
    if (rest === null || rest.n <= 0) {
      return { kind: "sum", group: groupName, sum: ratValue(known) };
    }
    return { probs: group.map((node) => node.prob ?? rest) };
  }
  if (!ratEquals(known, ONE)) {
    return { kind: "sum", group: groupName, sum: ratValue(known) };
  }
  return { probs: group.map((node) => node.prob as Rational) };
}

/** Exact sums overflowed: compare as decimals with a tolerance. */
function approximateSum(
  group: DraftNode[],
  groupName: string
): { probs: Rational[] } | TreeError {
  const total = group.reduce(
    (sum, node) => sum + (node.prob ? ratValue(node.prob) : 0),
    0
  );
  const blank = group.some((node) => node.prob === null);
  if (blank || Math.abs(total - 1) > 1e-9) {
    return { kind: "sum", group: groupName, sum: total };
  }
  return { probs: group.map((node) => node.prob as Rational) };
}

function finish(
  group: DraftNode[],
  groupName: string
): { nodes: TreeNode[] } | TreeError {
  const settled = settle(group, groupName);
  if ("kind" in settled) {
    return settled;
  }
  const nodes: TreeNode[] = [];
  for (const [index, draft] of group.entries()) {
    let children: TreeNode[] = [];
    if (draft.children.length > 0) {
      const inner = finish(draft.children, draft.label);
      if ("kind" in inner) {
        return inner;
      }
      children = inner.nodes;
    }
    nodes.push({ label: draft.label, prob: settled.probs[index], children });
  }
  return { nodes };
}

export function countLeaves(roots: readonly TreeNode[]): number {
  let total = 0;
  for (const node of roots) {
    total += node.children.length === 0 ? 1 : countLeaves(node.children);
  }
  return total;
}

function checkSize(roots: TreeNode[]): TreeResult {
  const leaves = countLeaves(roots);
  if (leaves > MAX_LEAVES) {
    return { ok: false, error: { kind: "tooMany", leaves } };
  }
  return { ok: true, roots };
}

/** The outline mode: one branch per line, children indented under their parent. */
export function parseOutline(text: string, startLabel: string): TreeResult {
  const drafts = draftLines(text);
  if ("kind" in drafts) {
    return { ok: false, error: drafts };
  }
  if (drafts.length === 0) {
    return { ok: false, error: { kind: "empty" } };
  }
  const done = finish(nest(drafts), startLabel);
  if ("kind" in done) {
    return { ok: false, error: done };
  }
  return checkSize(done.nodes);
}

/* ------------------------------------------------------------ repeated stages */

function repeat(level: TreeNode[], stages: number): TreeNode[] {
  if (stages <= 1) {
    return level.map((node) => ({ ...node, children: [] }));
  }
  return level.map((node) => ({
    ...node,
    children: repeat(level, stages - 1),
  }));
}

/** The same outcomes at every stage (coin tosses, draws with replacement). */
export function independentStages(
  text: string,
  stages: number,
  startLabel: string
): TreeResult {
  if (!Number.isInteger(stages) || stages < 1 || stages > MAX_STAGES) {
    return { ok: false, error: { kind: "stages" } };
  }
  const drafts = draftLines(text);
  if ("kind" in drafts) {
    return { ok: false, error: drafts };
  }
  if (drafts.length === 0) {
    return { ok: false, error: { kind: "empty" } };
  }
  const flat = drafts.map((draft) => ({ ...draft, indent: 0 }));
  const done = finish(flat, startLabel);
  if ("kind" in done) {
    return { ok: false, error: done };
  }
  if (done.nodes.length ** stages > MAX_LEAVES) {
    return {
      ok: false,
      error: { kind: "tooMany", leaves: done.nodes.length ** stages },
    };
  }
  return { ok: true, roots: repeat(done.nodes, stages) };
}

/* ----------------------------------------------------------------- bag draws */

const COUNT_LINE = /^(.*?)(?:\s*[:=]\s*|\s+)(\d+)$/;

interface Item {
  label: string;
  count: number;
}

function parseItems(text: string): Item[] | TreeError {
  const items: Item[] = [];
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    if (raw.trim() === "") {
      continue;
    }
    const match = COUNT_LINE.exec(raw.trim());
    if (!match || match[1].trim() === "") {
      return { kind: "line", line: index + 1 };
    }
    const count = Number(match[2]);
    if (count < 1) {
      return { kind: "counts" };
    }
    items.push({ label: match[1].trim(), count });
  }
  return items;
}

function drawLevel(
  counts: readonly number[],
  items: readonly Item[],
  remaining: number,
  replace: boolean
): TreeNode[] {
  const total = counts.reduce((sum, count) => sum + count, 0);
  const nodes: TreeNode[] = [];
  for (const [index, count] of counts.entries()) {
    const prob = count > 0 ? fraction(count, total) : null;
    if (prob === null) {
      continue;
    }
    let children: TreeNode[] = [];
    if (remaining > 1) {
      const next = replace
        ? counts
        : counts.map((c, i) => (i === index ? c - 1 : c));
      children = drawLevel(next, items, remaining - 1, replace);
    }
    nodes.push({ label: items[index].label, prob, children });
  }
  return nodes;
}

/** Draws from a bag of counted items; branch probabilities come from the counts. */
export function bagDraws(
  text: string,
  draws: number,
  replace: boolean
): TreeResult {
  if (!Number.isInteger(draws) || draws < 1 || draws > MAX_DRAWS) {
    return { ok: false, error: { kind: "draws" } };
  }
  const items = parseItems(text);
  if (!Array.isArray(items)) {
    return { ok: false, error: items };
  }
  if (items.length === 0) {
    return { ok: false, error: { kind: "empty" } };
  }
  if (items.length > MAX_ITEM_TYPES) {
    return { ok: false, error: { kind: "counts" } };
  }
  const total = items.reduce((sum, item) => sum + item.count, 0);
  if (!replace && draws > total) {
    return { ok: false, error: { kind: "draws" } };
  }
  const bound = items.length ** draws;
  if (bound > MAX_LEAVES * 4) {
    return { ok: false, error: { kind: "tooMany", leaves: bound } };
  }
  const roots = drawLevel(
    items.map((item) => item.count),
    items,
    draws,
    replace
  );
  return checkSize(roots);
}

/* -------------------------------------------------------------------- leaves */

/** Every outcome, in diagram order, with its exact and decimal probability. */
export function listLeaves(roots: readonly TreeNode[]): TreeLeaf[] {
  const leaves: TreeLeaf[] = [];
  const walk = (
    nodes: readonly TreeNode[],
    ids: number[],
    labels: string[],
    branches: Rational[]
  ) => {
    for (const [index, node] of nodes.entries()) {
      const nextIds = [...ids, index];
      const nextLabels = [...labels, node.label];
      const nextBranches = [...branches, node.prob];
      if (node.children.length > 0) {
        walk(node.children, nextIds, nextLabels, nextBranches);
        continue;
      }
      let exact: Rational | null = ONE;
      let value = 1;
      for (const branch of nextBranches) {
        exact = exact ? ratMul(exact, branch) : null;
        value *= ratValue(branch);
      }
      leaves.push({
        id: nextIds.join("/"),
        labels: nextLabels,
        branches: nextBranches,
        exact,
        value,
      });
    }
  };
  walk(roots, [], [], []);
  return leaves;
}

export interface EventProbability {
  value: number;
  exact: Rational | null;
  count: number;
}

/** The addition rule: P(event) is the sum of the selected outcomes' probabilities. */
export function eventProbability(
  leaves: readonly TreeLeaf[],
  selected: ReadonlySet<string>
): EventProbability {
  let value = 0;
  let exact: Rational | null = ZERO;
  let count = 0;
  for (const leaf of leaves) {
    if (!selected.has(leaf.id)) {
      continue;
    }
    count += 1;
    value += leaf.value;
    exact = exact && leaf.exact ? ratAdd(exact, leaf.exact) : null;
  }
  return { value, exact, count };
}
